import { formatNumber } from './helpers';

// Default measurement system
let measurementSystem = 'metric';

/** Function to set the measurement system from user context ('metric' or 'imperial') */
export const setMeasurementSystem = system => (measurementSystem = system);

/**
 * Number of square meters in one hectare.
 * Shared constant for any code that converts raw m² values (e.g. hexagon area)
 * into hectares, so the conversion factor is defined in a single place.
 */
export const SQUARE_METERS_PER_HECTARE = 10_000;

// Base value factors for length conversions
const METERS_TO_METERS_FACTOR = 1;
const METERS_TO_FEET_FACTOR = 3.2808398950131235;

// Base value factors for area conversions
const SQUARE_METERS_TO_SQUARE_METERS_FACTOR = 1;
const SQUARE_METERS_TO_SQUARE_FEET_FACTOR = 10.763910416709722;

/**
 * Configuration for measurement system conversions.
 *
 * This object defines the conversion settings for different measurement types (length and area)
 * across different measurement systems (metric and imperial).
 *
 * Each measurement type follows these conversion steps:
 * 1. Input values are always in base metric units (meters or square meters)
 *
 * 2. The value is converted to the target measurement system's base unit using baseValueConversionFactor
 *    - For metric: meters → meters or square meters → square meters (factor = 1)
 *    - For imperial: meters → feet or square meters → square feet (using conversion factors)
 *
 * 3. If unit scaling is enabled, the base value is compared against threshold values:
 *    - If baseValue ≥ threshold, it's divided by the corresponding divisor and uses the new unit
 *    - If baseValue < threshold, it keeps the base unit
 *
 * For example, with imperial length:
 * - 100 meters → 328.08 ft (below 5280ft threshold, stays as feet)
 * - 2000 meters → 6561.68 ft → 1.24 mi (above 5280ft threshold, converts to miles)
 *
 * @type {Object}
 * @property {Object} length - Length measurement configurations
 * @property {Object} length.metric - Metric length configuration (m → km)
 * @property {Object} length.imperial - Imperial length configuration (ft → mi)
 *
 * @property {Object} area - Area measurement configurations
 * @property {Object} area.metric - Metric area configuration (m² → km²)
 * @property {Object} area.imperial - Imperial area configuration (ft² → ac)
 */
const MEASUREMENT_SYSTEMS = Object.freeze({
	length: {
		metric: {
			baseUnit: 'm',
			baseValueConversionFactor: METERS_TO_METERS_FACTOR,
			conversions: [{ threshold: 1000, divisor: 1000, unit: 'km' }],
		},

		imperial: {
			baseUnit: 'ft',
			baseValueConversionFactor: METERS_TO_FEET_FACTOR,
			conversions: [{ threshold: 5280, divisor: 5280, unit: 'mi' }],
		},
	},

	area: {
		metric: {
			baseUnit: 'm²',
			baseValueConversionFactor: SQUARE_METERS_TO_SQUARE_METERS_FACTOR,
			conversions: [
				{ threshold: 10_000, divisor: 10_000, unit: 'ha' },
				{ threshold: 1_000_000, divisor: 1_000_000, unit: 'km²' },
			],
		},

		imperial: {
			baseUnit: 'ft²',
			baseValueConversionFactor: SQUARE_METERS_TO_SQUARE_FEET_FACTOR,
			conversions: [{ threshold: 43_560, divisor: 43_560, unit: 'ac' }],
		},
	},
});

/**
 * Resolves the scaling (divisor + unit) for an already-base-converted value:
 * the largest conversion whose threshold the value meets, else the base unit.
 * Split out from applyConversions so several values can be formatted against a
 * single shared scale (see formatAreasWithSharedUnit).
 *
 * @param {Array} conversions - conversion options ([{ threshold, divisor, unit }])
 * @param {number} baseValue - value already in the base unit
 * @param {string} baseUnit - the base unit (e.g. 'm²')
 * @returns {{ divisor: number, unit: string }}
 */
const resolveScale = (conversions, baseValue, baseUnit) => {
	let divisor = 1;
	let unit = baseUnit;
	for (const conversion of conversions) {
		if (Math.abs(baseValue) < conversion.threshold) break;
		divisor = conversion.divisor;
		unit = conversion.unit;
	}
	return { divisor, unit };
};

/**
 * Applies appropriate value and unit scaling based on the provided conversions.
 * @param {Array} conversions - Array of conversion options containing threshold, divisor, and unit (e.g., [{ threshold: 1000, divisor: 1000, unit: 'km' }])
 * @param {number} baseValue - The base value to format (eg., 1234)
 * @param {string} baseUnit - The base unit (e.g., 'm', 'ft', 'm²', 'ft²')
 * @returns {Array} [resultValue, resultUnit] (e.g., [1.234, 'km'])
 */
const applyConversions = (conversions, baseValue, baseUnit) => {
	const { divisor, unit } = resolveScale(conversions, baseValue, baseUnit);
	return [baseValue / divisor, unit];
};

/**
 * Resolves the measurement system config (base unit, conversion factor,
 * scaling thresholds) for a measurement type under the current measurement
 * system, falling back to metric if the current system has no config for it.
 *
 * @param {string} measurementType - Type of measurement ('length' or 'area')
 * @returns {Object} The resolved measurement system config
 * @throws {Error} If an invalid measurement type is provided
 */
const getMeasurementSystemConfig = measurementType => {
	const measurementTypeConfig = MEASUREMENT_SYSTEMS[measurementType] ?? null;
	if (!measurementTypeConfig) throw new Error('Invalid measurement type');

	return measurementTypeConfig[measurementSystem] ?? measurementTypeConfig.metric;
};

/**
 * Formats measurement values with appropriate units based on measurement type and system.
 * Applies unit scaling when appropriate and formats the number with comma separators.
 *
 * @param {string} measurementType - Type of measurement ('length' or 'area')
 * @param {number} value - The measurement value to format (assumed to be in base metric units: meters or square meters)
 * @param {number} decimalPlaces - Number of decimal places to display in the result
 * @param {boolean} allowUnitScaling - Whether to scale units based on value magnitude
 *                                             (e.g., m to km or ft to mi for large values)
 * @returns {Array} Array containing [formattedNumberString, unitSymbol]
 * @throws {Error} If an invalid measurement type is provided
 */
const formatMeasurement = (measurementType, value, decimalPlaces, allowUnitScaling) => {
	const { baseUnit, baseValueConversionFactor, conversions } =
		getMeasurementSystemConfig(measurementType);

	// Convert the value to the base unit according to the measurement system (meters for metric, feet for imperial)
	const baseValue = value * baseValueConversionFactor;

	// If allowUnitScaling is true, it will check conversion thresholds and apply unit scaling if threshold is met
	// Else, it will return the base value and unit
	// (e.g., [1000, 'm'] => [1, 'km'])
	const [resultValue, resultUnit] = allowUnitScaling
		? applyConversions(conversions, baseValue, baseUnit)
		: [baseValue, baseUnit];

	// Returns the formatted number and correct unit according to the measurement system
	return [formatNumber(resultValue, decimalPlaces), resultUnit];
};

/**
 * Formats a length measurement with appropriate units based on the current measurement system.
 * Uses the globally set measurement system (metric or imperial) and applies unit scaling when specified.
 *
 * @param {number} meters - The length value in meters to format
 * @param {number} [decimalPlaces=1] - Number of decimal places to display in the formatted result
 * @param {boolean} [allowUnitScaling=true] - Whether to automatically scale units based on value magnitude
 *                                            (e.g., m to km or ft to mi for large values)
 * @returns {Array} Array containing [formattedNumberString, unitSymbol]
 *
 * @example
 * // With measurement system set to 'metric'
 * formatLength(1500); // Returns ["1,500.00", "m"]
 * formatLength(1500, 1, true); // Returns ["1.5", "km"]
 *
 * @example
 * // With measurement system set to 'imperial'
 * formatLength(1500); // Returns ["4,921.26", "ft"]
 * formatLength(1500, 2, true); // Returns ["0.93", "mi"]
 */
export const formatLength = (meters, decimalPlaces = 1, allowUnitScaling = true) =>
	formatMeasurement('length', meters, decimalPlaces, allowUnitScaling);

/**
 * Formats an area measurement with appropriate units based on the current measurement system.
 * Uses the globally set measurement system (metric or imperial) and applies unit scaling when specified.
 *
 * @param {number} squaredMeters - The area value in square meters to format
 * @param {number} [decimalPlaces=1] - Number of decimal places to display in the formatted result
 * @param {boolean} [allowUnitScaling=true] - Whether to automatically scale units based on value magnitude
 *                                            (e.g., m² to km² or ft² to ac for large values)
 * @returns {Array} Array containing [formattedNumberString, unitSymbol]
 *
 * @example
 * // With measurement system set to 'metric'
 * formatArea(5000); // Returns ["5,000.00", "m²"]
 * formatArea(2000000, 1, true); // Returns ["2.0", "km²"]
 *
 * @example
 * // With measurement system set to 'imperial'
 * formatArea(5000); // Returns ["53,819.55", "ft²"]
 * formatArea(10000, 3, true); // Returns ["2.471", "ac"]
 */
export const formatArea = (squaredMeters, decimalPlaces = 1, allowUnitScaling = true) =>
	formatMeasurement('area', squaredMeters, decimalPlaces, allowUnitScaling);

/**
 * Formats several area values with a SINGLE shared unit, chosen from the largest
 * value, so they can be compared directly. A per-value `formatArea` scales each
 * value independently and can show mixed units in one row (e.g. "1,261 m²" next
 * to "1.5 ha"), which defeats side-by-side comparison. Respects the current
 * measurement system.
 *
 * @param {Array<number|null|undefined>} squaredMetersValues - areas in base m²
 * @param {number} [decimalPlaces=1]
 * @returns {{ unit: string, values: string[] }} the shared unit and formatted
 *   number strings aligned to the input order
 *
 * @example
 * // metric
 * formatAreasWithSharedUnit([1261.3, 15000]); // { unit: 'ha', values: ['0.1', '1.5'] }
 * formatAreasWithSharedUnit([1261.3, 5000]);  // { unit: 'm²', values: ['1,261.3', '5,000.0'] }
 */
export const formatAreasWithSharedUnit = (squaredMetersValues = [], decimalPlaces = 1) => {
	const { baseUnit, baseValueConversionFactor, conversions } = getMeasurementSystemConfig('area');
	const baseValues = squaredMetersValues.map(value => (value ?? 0) * baseValueConversionFactor);
	const maxBaseValue = baseValues.reduce((max, value) => Math.max(max, Math.abs(value)), 0);
	const { divisor, unit } = resolveScale(conversions, maxBaseValue, baseUnit);

	return {
		unit,
		values: baseValues.map(value => formatNumber(value / divisor, decimalPlaces)),
	};
};

/**
 * Gets the base unit symbol for a measurement type under the current measurement
 * system, without formatting any value (e.g. always 'm'/'ft' or 'm²'/'ft²',
 * never the scaled 'km'/'mi'/'ha'/'ac' units).
 *
 * @param {'length'|'area'} measurementType
 * @returns {string} The base unit symbol
 * @throws {Error} If an invalid measurement type is provided
 */
export const getBaseUnit = measurementType => getMeasurementSystemConfig(measurementType).baseUnit;

/**
 * Gets the conversion factor from base metric (meters / m²) to the current
 * measurement system's base unit. Multiply a metric value by this factor to
 * get the display value; divide a display value by it to get back to metric.
 *
 * @param {'length'|'area'} measurementType
 * @returns {number} The conversion factor (1 for metric, ~3.28 for imperial length, ~10.76 for imperial area)
 */
export const getConversionFactor = measurementType =>
	getMeasurementSystemConfig(measurementType).baseValueConversionFactor;

/**
 * Converts a measurement in meters to feet.
 * @param {number} meters - The distance in meters to convert.
 * @returns {number} The distance converted to feet.
 */
export const meterToFt = meters => meters * METERS_TO_FEET_FACTOR;

/**
 * Converts an area measurement from square meters to square feet.
 * @param {number} squareMeters - The area in square meters to convert.
 * @returns {number} The converted area in square feet.
 */
export const squareMeterToSquareFt = squareMeters =>
	squareMeters * SQUARE_METERS_TO_SQUARE_FEET_FACTOR;

/**
 * Converts square kilometers to square feet.
 * @param {number} squareKilometers - The area in square kilometers to convert.
 * @returns {number} The converted area in square feet.
 */
export const squareKilometerToSquareFt = squareKilometers =>
	squareKilometers * 1_000_000 * SQUARE_METERS_TO_SQUARE_FEET_FACTOR;

/**
 * Converts feet to meters.
 * @param {number} feet - The distance in feet to convert.
 * @returns {number} The equivalent distance in meters.
 */
export const ftToMeter = feet => feet / METERS_TO_FEET_FACTOR;

/**
 * Converts an area measurement from square feet to square meters.
 * @param {number} squareFeet - The area in square feet to be converted.
 * @returns {number} The converted area in square meters.
 */
export const squareFtToSquareMeter = squareFeet => squareFeet / SQUARE_METERS_TO_SQUARE_FEET_FACTOR;

/**
 * Converts acres to square meters.
 * @param {number} acres - The area in acres to convert.
 * @returns {number} The converted area in square meters.
 */
export const acresToSquareMeter = acres => (acres * 43_560) / SQUARE_METERS_TO_SQUARE_FEET_FACTOR;
